import { useSearchParams, useNavigate, useLocation, useParams, Outlet } from 'react-router-dom';
import { ErrorDetailsDtoErrorCodeEnum, ResponseErrorDto } from '@interfaces/models';
import { getCostCentersPaginated, getCostCenter } from '@api/cost-centers';
import { PaymentStatus } from '@shared/components/payu';
import { useQuery } from '@tanstack/react-query';
import { Loading, toast } from '@ui/components';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, FC } from 'react';
import { Routes } from '@shared/enums';

const FIRST_PAGE = {
  pagination: {
    pageIndex: 0,
    pageSize: 50,
  },
};

export const CostCenterRoute: FC = () => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const paymentStatus = searchParams.get('status') as PaymentStatus;
  const location = useLocation();

  const costCenterId = params.costCenterId;
  const paramsCompanyId = params.companyId;

  // Get first page of cost centers to check if there is only one cost center
  const { data: costCentersList, isLoading: isCostCentersFetching } = useQuery({
    queryKey: ['cost-centers', FIRST_PAGE, paramsCompanyId],
    queryFn: () =>
      getCostCentersPaginated(FIRST_PAGE).catch((err: { error: ResponseErrorDto }) => {
        if (err.error.errorCode === ErrorDetailsDtoErrorCodeEnum.PAYMENT_API_PAYMENT_ERROR)
          toast.error(t('API_ERRORS.PAYMENT_API_PAYMENT_ERROR'));
        throw err;
      }),
  });

  // Get costCenterId entity to check if it exists
  const {
    data: costCenter,
    isLoading: isCostCenterFetching,
    isError: isCostCenterError,
  } = useQuery({
    queryKey: ['cost-center', costCenterId, paramsCompanyId],
    queryFn: () => getCostCenter(costCenterId!),
    enabled: !!costCenterId,
  });

  const canNavigateToList = useMemo(
    () => costCentersList?.pagination.totalItems && costCentersList?.pagination.totalItems > 1,
    [costCentersList],
  );

  useEffect(() => {
    if (paymentStatus === 'SUCCESS') {
      toast.success(t('COST_CENTER.PAYMENT_SUCCESS'));
    }
    if (paymentStatus === 'FAILURE' || paymentStatus === 'TIMEOUT') {
      toast.error(t('COST_CENTER.PAYMENT_FAILURE'));
    }
    // Navigate to default cost center if there is only one cost center
    if (!costCenterId && costCentersList?.data.length === 1 && costCentersList?.data[0]) {
      navigate(`${costCentersList?.data[0].id}`, { replace: true, state: { ...location.state } });
    }
    // Navigate to cost centers list if costCenterId entity does not exist
    if (costCenterId && isCostCenterError) {
      navigate(`../${Routes.COST_CENTER}`, { replace: true });
    }
  }, [costCenterId, costCentersList, navigate, isCostCenterError, paymentStatus, t, location]);

  if (isCostCentersFetching || isCostCenterFetching || isCostCenterError) return <Loading />;

  return <Outlet context={costCenterId ? { costCenter, canNavigateToList } : {}} />;
};
